import { useState, useEffect, useCallback, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  RadioGroup,
  Radio,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@coreai/fluentui-react';
import { DismissRegular } from '@fluentui/react-icons';

import { UserSearchDropdown, SelectedUserDisplay, type User } from './UserComponents';
import { AccessResponse, Permission } from '@/types/permissions';

const useStyles = makeStyles({
  dialogSurface: {
    // Compact, focused mini-form. 520px keeps the dialog from feeling
    // heavyweight while still giving the Role radio list room to breathe.
    width: '520px',
    maxWidth: '90vw',
  },
  dialogSection: {
    marginBottom: tokens.spacingVerticalL,
  },
  dialogSectionTitle: {
    marginBottom: tokens.spacingVerticalS,
  },
  groupAssignmentNotice: {
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalS,
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    // Small gap reads as a group without the visual weight of hard dividers.
    gap: tokens.spacingVerticalXXS,
  },
  radioItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    // Comfortable hit area + concentric radius so the hover background
    // feels like a button rather than a stripe across the dialog.
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    // Interruptible — specify exact properties (never transition: all).
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  radioDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
  },
  subtleText: {
    color: tokens.colorNeutralForeground3,
  },
  messageBar: {
    marginBottom: tokens.spacingVerticalS,
  },
});

export interface RoleAssignmentDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Whether the dialog was opened to add or edit an assignment */
  mode: 'add' | 'edit';
  /** Callback when the dialog is closed */
  onClose: () => void;
  /** The dialog title */
  title?: string;
  /** The submit button label */
  submitLabel?: string;
  /** The mount node for the dialog (should be the parent dialog/modal element) */
  mountNode?: HTMLElement | null | undefined;
  /** List of supported permissions that can be assigned */
  supportedPermissions: Permission[];
  /** Function to check access for a specific user by ID */
  checkAccess: (userId: string) => Promise<AccessResponse>;
  /** Callback when assignment is submitted */
  onSubmit: (user: User, permissions: string[]) => Promise<void>;
  /** Initial user to pre-populate (for edit mode) */
  initialUser?: User | null;
};

/**
 * RoleAssignmentDialog - A centered modal dialog for assigning permissions to a user
 * Supports customizable title and submit button label
 */
export function RoleAssignmentDialog({
  open,
  mode,
  onClose,
  title,
  submitLabel,
  mountNode,
  supportedPermissions,
  checkAccess,
  onSubmit,
  initialUser,
}: RoleAssignmentDialogProps): JSX.Element {
  const styles = useStyles();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedPermission, setSelectedPermission] = useState<string | null>(null);
  const [initialPermission, setInitialPermission] = useState<string | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const accessRequestIdRef = useRef(0);
  const userSearchRef = useRef<HTMLInputElement>(null);
  const shouldRestoreSearchFocusRef = useRef(false);

  const handleUserSelect = useCallback(async (user: User) => {
    const requestId = ++accessRequestIdRef.current;
    setSelectedUser(user);
    setSelectedPermission(null);
    setInitialPermission(null);
    setAccessError(null);
    setSubmitError(null);
    setIsCheckingAccess(true);

    try {
      const access = await checkAccess(user.id);
      if (requestId !== accessRequestIdRef.current) return;

      const permissionId = access.permissions[0]?.id ?? null;
      setSelectedPermission(permissionId);
      setInitialPermission(permissionId);
    } catch {
      if (requestId !== accessRequestIdRef.current) return;
      setAccessError(
        mode === 'add'
          ? "We couldn't check this user's current access. Clear the selection and try again."
          : "We couldn't check this user's current access. Close the dialog and try again.",
      );
    } finally {
      if (requestId === accessRequestIdRef.current) {
        setIsCheckingAccess(false);
      }
    }
  }, [checkAccess, mode]);

  useEffect(() => {
    if (!open) return;
    // Reset every time the dialog opens (not on close) so the exit
    // animation doesn't briefly flash the "search user" state before
    // the surface unmounts. In edit mode, pre-populate from initialUser.
    setSubmitError(null);
    if (initialUser) {
      void handleUserSelect(initialUser);
    } else {
      accessRequestIdRef.current += 1;
      setSelectedUser(null);
      setSelectedPermission(null);
      setInitialPermission(null);
      setAccessError(null);
      setIsCheckingAccess(false);
    }
    // Intentionally narrow to `initialUser?.id`: depending on the object
    // reference would re-fire `handleUserSelect` (and its `checkAccess`
    // network call) whenever the parent recreates the User on a refetch,
    // even when the underlying user is unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialUser?.id, handleUserSelect]);

  const handlePermissionChange = (permissionId: string) => {
    setSelectedPermission(permissionId);
    setSubmitError(null);
  };

  const handleUserClear = useCallback(() => {
    accessRequestIdRef.current += 1;
    setSelectedUser(null);
    setSelectedPermission(null);
    setInitialPermission(null);
    setAccessError(null);
    setSubmitError(null);
    setIsCheckingAccess(false);
    shouldRestoreSearchFocusRef.current = true;
  }, []);

  useEffect(() => {
    if (selectedUser !== null || !shouldRestoreSearchFocusRef.current) return;

    const animationFrame = window.requestAnimationFrame(() => {
      userSearchRef.current?.focus();
      shouldRestoreSearchFocusRef.current = false;
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [selectedUser]);

  const hasPermissionChanges = selectedUser !== null && selectedPermission !== initialPermission;
  const hasExistingAssignment = selectedUser !== null && initialPermission !== null;
  const isUpdateOperation = mode === 'edit' || (mode === 'add' && hasExistingAssignment);
  const resolvedTitle = mode === 'add' && hasExistingAssignment
    ? 'Update role assignment'
    : (title ?? (isUpdateOperation ? 'Update role assignment' : 'Add role assignment'));
  const resolvedSubmitLabel = mode === 'add' && hasExistingAssignment
    ? 'Update'
    : (submitLabel ?? (isUpdateOperation ? 'Update' : 'Add'));
  const existingPermissionName = supportedPermissions.find(
    (permission) => permission.id === initialPermission,
  )?.name ?? initialPermission;
  const accessStatusMessage = selectedUser && hasExistingAssignment && existingPermissionName
    ? mode === 'add'
      ? `${selectedUser.displayName} already has ${existingPermissionName} access. Select a different role to update their access, or clear the user to choose someone else.`
      : `${selectedUser.displayName} currently has ${existingPermissionName} access. Select a different role to enable Update.`
    : null;

  const handleClose = () => {
    if (isSubmitting) return;
    // Do NOT clear local state here; the close animation would briefly
    // re-render the dialog in its empty "Add user" state. The next
    // open-effect resets everything fresh.
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!selectedUser || !selectedPermission) return;

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(selectedUser, [selectedPermission]);
      // Same reasoning as handleClose: let the next open reset state.
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to update permissions. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_, { open: isOpen }) => !isOpen && handleClose()}
    >
      <DialogSurface
        className={styles.dialogSurface}
        {...(mountNode !== undefined ? { mountNode } : {})}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close dialog"
                icon={<DismissRegular />}
                onClick={handleClose}
                disabled={isSubmitting}
              />
            }
          >
            {resolvedTitle}
          </DialogTitle>
          <DialogContent>
            {/*
              Keep the live region mounted before either async error occurs so
              screen readers reliably announce the update (WCAG 4.1.3).
            */}
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {(submitError || accessError) && (
                <MessageBar intent="error" className={styles.messageBar}>
                  <MessageBarBody>{submitError ?? accessError}</MessageBarBody>
                </MessageBar>
              )}
            </div>
            {/* User Selection */}
            <div className={styles.dialogSection}>
              <Text weight="semibold" className={styles.dialogSectionTitle} block>
                Select user
              </Text>
              {!selectedUser ? (
                <>
                  <Text size={200} className={styles.groupAssignmentNotice} block>
                    Entra groups are not supported because group assignments do not grant member access.
                  </Text>
                  <UserSearchDropdown
                    onUserSelect={handleUserSelect}
                    placeholder="Search for a user to assign..."
                    inputRef={userSearchRef}
                    ariaLabel="Search user to assign"
                  />
                </>
              ) : (
                <SelectedUserDisplay
                  user={selectedUser}
                  {...(mode === 'add' ? { onClear: handleUserClear } : {})}
                  actionsDisabled={isSubmitting}
                />
              )}
            </div>

            {/* Permissions Selection */}
            {selectedUser && (
              <div className={styles.dialogSection}>
                <Text id="role-selection-label" weight="semibold" className={styles.dialogSectionTitle} block>
                  Role
                </Text>
                {isCheckingAccess ? (
                  <Spinner size="small" label="Checking current access..." />
                ) : accessError ? (
                  null
                ) : (
                  <>
                    <div role="status" aria-live="polite" aria-atomic="true">
                      {accessStatusMessage && (
                        <MessageBar intent="info" className={styles.messageBar}>
                          <MessageBarBody>{accessStatusMessage}</MessageBarBody>
                        </MessageBar>
                      )}
                    </div>
                    <RadioGroup
                      className={styles.radioGroup}
                      value={selectedPermission ?? ''}
                      onChange={(_, data) => handlePermissionChange(data.value)}
                      disabled={isSubmitting}
                      // The visible "Role" heading is a sibling <Text>, not a Field
                      // label, so wire it explicitly for AT (WCAG 4.1.2 Name, Role,
                      // Value and 1.3.1 Info and Relationships).
                      aria-labelledby="role-selection-label"
                    >
                      {supportedPermissions.map((permission) => (
                        /*
                          Use a <label> element so a click anywhere in the
                          row (name + description) selects the Radio via
                          implicit form-control association. The Radio
                          keeps its `aria-labelledby` / `aria-describedby`
                          wiring so AT still announces a single name +
                          description on focus.
                        */
                        <label key={permission.id} className={styles.radioItem}>
                          <Radio
                            value={permission.id}
                            aria-labelledby={`role-name-${permission.id}`}
                            aria-describedby={`role-desc-${permission.id}`}
                          />
                          <div className={styles.radioDetails}>
                            <Text id={`role-name-${permission.id}`} weight="medium">{permission.name}</Text>
                            <Text id={`role-desc-${permission.id}`} size={200} className={styles.subtleText}>
                              {permission.description}
                            </Text>
                          </div>
                        </label>
                      ))}
                    </RadioGroup>
                  </>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!hasPermissionChanges || isSubmitting}
              onClick={handleSubmit}
              {...(isSubmitting ? { icon: <Spinner size="tiny" /> } : {})}
            >
              {isSubmitting ? 'Saving…' : resolvedSubmitLabel}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

RoleAssignmentDialog.displayName = 'RoleAssignmentDialog';
